import { whenOnce } from "@arcgis/core/core/reactiveUtils";
import Point from "@arcgis/core/geometry/Point";
import { WORLD_MAP_CLUSTER_FIT_PADDING_PX, WORLD_MAP_CLUSTER_MAX_ZOOM, WORLD_MAP_CLUSTER_ZOOM_MAX_DURATION_MS } from "@components/world-map/clustering/world-map-cluster-constants";
import { getMarkerPopupHorizontalPlacement, getMarkerPopupPlacement, isAbortError, isClusterGraphic } from "@components/world-map/world-map-utils";

/** Vertical and horizontal popup placement relative to a marker. */

/** Amount to increase zoom when clicking into a cluster. */
const CLUSTER_ZOOM_INCREASE = 3;

/**
 * Computes vertical and horizontal popup placement for a marker
 * given its screen position and the map view dimensions.
 */
export function getPopupPlacement(screenPoint, viewWidth, viewHeight) {
  const placement = getMarkerPopupPlacement(screenPoint.y, viewHeight);
  if (placement === "center") {
    return {
      placement,
      horizontalPlacement: screenPoint.x <= viewWidth / 2 ? "right" : "left"
    };
  }
  return {
    placement,
    horizontalPlacement: getMarkerPopupHorizontalPlacement(screenPoint.x, viewWidth)
  };
}

/**
 * Hit-tests the map at the given event position and returns the first layer graphic.
 * Clusters win over individual markers so a bubble is never shadowed by a pin
 * drawn underneath it.
 */
export async function getAnyLayerGraphic(view, layers, event) {
  const hitTest = layers.length > 0 ? await view.hitTest(event, {
    include: layers
  }) : await view.hitTest(event);
  const graphics = hitTest.results.flatMap(result => result.type === "graphic" ? [result.graphic] : []);
  return graphics.find(isClusterGraphic) ?? graphics[0] ?? null;
}

/** Ground metres per degree of latitude, and per degree of longitude at the equator. */
const METERS_PER_DEGREE = 111320;

/** Multiplier to convert an angle in degrees to radians (Math.PI / 180). */
const DEGREES_TO_RADIANS = 0.017453292519943295;

/** Ground metres per screen pixel at zoom 0 (the 256px tile scheme ArcGIS zoom levels follow). */
const METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392804097;

/** Bounding box of the entities' positions, in degrees. */
function getEntitiesExtent(entities) {
  const lngs = entities.map(entity => entity.position.lng);
  const lats = entities.map(entity => entity.position.lat);
  return {
    xmin: Math.min(...lngs),
    ymin: Math.min(...lats),
    xmax: Math.max(...lngs),
    ymax: Math.max(...lats)
  };
}

/**
 * Zoom at which the extent fills the view, less the fit padding.
 * Capped just past {@link WORLD_MAP_CLUSTER_MAX_ZOOM}: any deeper adds nothing,
 * because clustering is already off there. That cap also covers members at the
 * same spot, whose tiny box would otherwise zoom in all the way.
 */
function getFitZoom(extent, viewWidth, viewHeight) {
  const centerLat = (extent.ymin + extent.ymax) / 2;
  const widthMeters = (extent.xmax - extent.xmin) * METERS_PER_DEGREE * Math.cos(centerLat * DEGREES_TO_RADIANS);
  const heightMeters = (extent.ymax - extent.ymin) * METERS_PER_DEGREE;
  const usableWidth = viewWidth - 2 * WORLD_MAP_CLUSTER_FIT_PADDING_PX;
  const usableHeight = viewHeight - 2 * WORLD_MAP_CLUSTER_FIT_PADDING_PX;
  const metersPerPixel = Math.max(widthMeters / usableWidth, heightMeters / usableHeight);
  const maxZoom = WORLD_MAP_CLUSTER_MAX_ZOOM + 1;
  if (metersPerPixel <= 0) {
    return maxZoom;
  }
  return Math.min(Math.log2(METERS_PER_PIXEL_AT_ZOOM_0 / metersPerPixel), maxZoom);
}

/**
 * Frames all members of a clicked cluster so their bounding box fills the view.
 * Clustering then runs again at the new zoom, so members that are still close
 * together form smaller clusters and the rest show as individual pins.
 *
 * The zoom is computed here instead of passing the points to `goTo` as a target:
 * the 3D view frames a target generously and leaves a wide gap around it.
 */
export async function zoomToClusterEntities(view, entities) {
  const extent = getEntitiesExtent(entities);
  const center = new Point({
    longitude: (extent.xmin + extent.xmax) / 2,
    latitude: (extent.ymin + extent.ymax) / 2
  });
  try {
    await view.goTo({
      center,
      zoom: getFitZoom(extent, view.width, view.height)
    }, {
      maxDuration: WORLD_MAP_CLUSTER_ZOOM_MAX_DURATION_MS,
      // Starts at full speed and slows into the target, so the map reacts to
      // the click at once. The 3D default eases in slowly and looks stuck.
      easing: "out-cubic"
    });
  } catch (error) {
    // Panning or zooming during the flight cancels it; that is not a failure.
    if (!isAbortError(error)) {
      throw error;
    }
  }
}

/**
 * Zooms into a cluster graphic center to reveal individual markers.
 * Waits for the view to settle, then refreshes the layer to clear cached cluster graphics.
 */
export async function zoomIntoCluster(view, layer, clusterGraphic) {
  await view.goTo({
    center: clusterGraphic.geometry,
    zoom: view.zoom + CLUSTER_ZOOM_INCREASE
  });

  // Wait until the view is fully settled after the zoom animation.
  if (!view.stationary) {
    await whenOnce(() => view.stationary);
  }

  // Force the layer to re-fetch its data and recalculate clustering from scratch.
  // Without this, ArcGIS sometimes caches the old cluster graphics and doesn't
  // re-evaluate them after a programmatic goTo.
  layer.refresh();
}